import { ArrayBufferTarget, Muxer } from 'mp4-muxer';
import { patchQuickTimeBrand, type BoxInfo, readTopLevelBoxes } from './mp4';
import type { ChunkLike } from './timeline';

/**
 * Single funnel for turning encoded chunks into a file.
 *
 * Timestamps come from our own normalized timeline rather than the chunk's raw
 * capture time, which is what makes lossless trimming possible: we simply hand
 * the muxer a different timestamp per chunk and it rebuilds the sample table.
 */

export interface VideoTrackSource {
  chunks: EncodedVideoChunk[];
  timeline: ChunkLike[];
  meta: EncodedVideoChunkMetadata | null;
  startIndex: number;
  endIndex: number;
  /** Timestamp that becomes 0 in the output file. */
  originUs: number;
}

export interface AudioTrackSource {
  chunks: EncodedAudioChunk[];
  timeline: ChunkLike[];
  meta: EncodedAudioChunkMetadata | null;
  startIndex: number;
  endIndex: number;
  originUs: number;
  codec: 'aac' | 'opus';
  channels: number;
  sampleRate: number;
}

export interface MuxOptions {
  container: 'mp4' | 'mov';
  width: number;
  height: number;
  fps: number;
  video: VideoTrackSource;
  audio: AudioTrackSource | null;
  onProgress?: (ratio: number) => void;
  signal?: AbortSignal;
}

export class ExportCancelled extends Error {
  constructor() {
    super('Export cancelled.');
    this.name = 'ExportCancelled';
  }
}

const nextTask = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

export async function muxToBuffer(options: MuxOptions): Promise<ArrayBuffer> {
  const { video, audio, container } = options;
  if (video.endIndex <= video.startIndex) {
    throw new Error('Nothing to export: the selected range contains no video.');
  }

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: {
      codec: 'avc',
      width: options.width,
      height: options.height,
      frameRate: options.fps,
    },
    audio: audio
      ? {
          codec: audio.codec,
          numberOfChannels: audio.channels,
          sampleRate: audio.sampleRate,
        }
      : undefined,
    // Keep the metadata up front so the file plays without a full download.
    fastStart: 'in-memory',
    // Both tracks share one clock; the video track's first chunk is always
    // added first, so the shared origin stays at zero.
    firstTimestampBehavior: 'cross-track-offset',
  });

  const total = video.endIndex - video.startIndex + (audio ? audio.endIndex - audio.startIndex : 0);
  let written = 0;
  const report = () => {
    if (options.onProgress && total > 0) options.onProgress(Math.min(written / total, 1));
  };

  const addVideo = (index: number, withMeta: boolean) => {
    const chunk = video.chunks[index];
    const timestamp = Math.max(0, video.timeline[index].timestamp - video.originUs);
    muxer.addVideoChunk(chunk, withMeta && video.meta ? video.meta : undefined, timestamp);
    written += 1;
  };

  const addAudio = (index: number, withMeta: boolean) => {
    const track = audio;
    if (!track) return;
    const chunk = track.chunks[index];
    const timestamp = Math.max(0, track.timeline[index].timestamp - track.originUs);
    muxer.addAudioChunk(chunk, withMeta && track.meta ? track.meta : undefined, timestamp);
    written += 1;
  };

  // The first video chunk always goes in first: it seeds the shared origin and
  // carries the decoder config the file needs.
  addVideo(video.startIndex, true);

  let videoIndex = video.startIndex + 1;
  let audioIndex = audio ? audio.startIndex : 0;
  let sinceYield = 0;

  while (videoIndex < video.endIndex || (audio && audioIndex < audio.endIndex)) {
    if (sinceYield > 500) {
      sinceYield = 0;
      report();
      await nextTask();
      if (options.signal?.aborted) throw new ExportCancelled();
    }
    sinceYield += 1;

    const videoTs = videoIndex < video.endIndex ? video.timeline[videoIndex].timestamp : Number.POSITIVE_INFINITY;
    const audioTs =
      audio && audioIndex < audio.endIndex ? audio.timeline[audioIndex].timestamp : Number.POSITIVE_INFINITY;

    if (videoTs <= audioTs) {
      addVideo(videoIndex, false);
      videoIndex += 1;
    } else {
      addAudio(audioIndex, audioIndex === (audio?.startIndex ?? 0));
      audioIndex += 1;
    }
  }

  report();
  muxer.finalize();
  const buffer = target.buffer;
  if (container === 'mov') patchQuickTimeBrand(buffer);
  return buffer;
}

/** Structural sanity check used by tests and by the export confirmation UI. */
export function inspectFile(buffer: ArrayBuffer): {
  boxes: BoxInfo[];
  brand: string | null;
  hasMoov: boolean;
  hasMdat: boolean;
} {
  const boxes = readTopLevelBoxes(buffer);
  const ftyp = boxes.find((box) => box.type === 'ftyp');
  const brand = ftyp
    ? String.fromCharCode(...new Uint8Array(buffer, ftyp.start + 8, 4))
    : null;
  return {
    boxes,
    brand,
    hasMoov: boxes.some((box) => box.type === 'moov'),
    hasMdat: boxes.some((box) => box.type === 'mdat'),
  };
}
